import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import Header from "../../components/ui/Header";
import { Breadcrumb } from "../../components/ui/Breadcrumb";
import EventDetailsForm from "./components/EventDetailsForm";
import ParticipantSearch from "./components/ParticipantSearch";
import ParticipantList from "./components/ParticipantList";
import CreateEventActions from "./components/CreateEventActions";
import apiService from "services/api";
import { getAuthData } from "utils/auth";

const CreateEvent = () => {
  const navigate = useNavigate();

  // Form state
  const [eventName, setEventName] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [eventDescription, setEventDescription] = useState("");
  const [budget, setBudget] = useState(""); // Added budget state
  const [searchEmail, setSearchEmail] = useState("");
  const [participants, setParticipants] = useState([]);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const { user: currentUser } = getAuthData();

  // Add current user as default participant
  useEffect(() => {
    setParticipants([currentUser]);
  }, []);

  // Form validation
  const validateForm = () => {
    const newErrors = {};

    if (!eventName?.trim()) {
      newErrors.eventName = "Event name is required";
    } else if (eventName?.trim()?.length < 3) {
      newErrors.eventName = "Event name must be at least 3 characters";
    }

    if (!eventDate) {
      newErrors.eventDate = "Event date is required";
    } else {
      const selectedDate = new Date(eventDate);
      const today = new Date();
      today?.setHours(0, 0, 0, 0);

      // Backend requires date to be greater than today (future dates only)
      if (selectedDate <= today) {
        newErrors.eventDate = "Event date must be in the future";
      }
    }

    if (participants?.length < 2) {
      newErrors.participants =
        "At least one additional participant is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  // Add participant handler (local only - no API call needed for new event)
  const handleAddParticipant = (user) => {
    // Check for duplicates locally - compare by email for existing users or by temporary flag for new users
    const isDuplicate = participants?.some(
      (p) => p?.email?.toLowerCase() === user?.email?.toLowerCase()
    );

    if (isDuplicate) {
      setErrors((prev) => ({
        ...prev,
        participants: "This participant is already added",
      }));
      return;
    }

    // Clear participant errors
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors?.participants;
      return newErrors;
    });

    // Add participant locally (no API call for new event)
    setParticipants((prev) => [...prev, user]);
  };

  // Remove participant handler (local only - no API call needed for new event)
  const handleRemoveParticipant = (participantId) => {
    // Prevent removing current user
    if (participantId === currentUser?.id) {
      return;
    }

    // Remove participant locally (no API call for new event)
    // For temporary users, we need to match by email since they don't have IDs
    setParticipants((prev) =>
      prev?.filter((p) => {
        if (p.id) {
          return p.id !== participantId;
        } else {
          // For temporary users without ID, we'll use email as identifier
          return p.email !== participantId;
        }
      })
    );
  };

  // Create event handler (includes participants)
  const handleCreateEvent = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Prepare participant data - separate existing users from new users
      const participantData = [];

      // Add current user as creator
      participantData.push(currentUser.id);

      // Process other participants
      participants
        .filter((p) => p.id !== currentUser?.id) // Remove current user if they were manually added
        .forEach((p) => {
          if (p.id && !p.isTemporary) {
            // Existing user with ID
            participantData.push(p.id);
          } else if (p.isTemporary) {
            // New user to be created - send object with name and email
            participantData.push({
              name: p.name,
              email: p.email,
            });
          }
        });

      const eventPayload = {
        name: eventName?.trim(),
        date: eventDate,
        description: eventDescription?.trim(),
        budget: budget ? parseFloat(budget) : 0, // Use the budget state value
        participantIds: participantData,
      };

      console.log("Creating event with payload:", eventPayload);

      // Create event via API (this will handle participant creation)
      const response = await apiService.createEvent(eventPayload);

      if (response.success) {
        console.log("Event created successfully:", response.data.event);

        // Show success message
        alert("Event created successfully!");

        // Navigate to the new event details page
        navigate(`/events/${response.data.event.id}`);
      }
    } catch (error) {
      console.error("Error creating event:", error);
      setErrors({
        submit: error.message || "Failed to create event. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Check if form is valid
  const isFormValid =
    eventName?.trim()?.length >= 3 &&
    eventDate &&
    participants?.length >= 2 &&
    Object.keys(errors)?.length === 0;

  // Custom breadcrumb items
  const breadcrumbItems = [
    { label: "Dashboard", path: "/dashboard", icon: "Home" },
    { label: "Create Event", path: "/create-event", icon: "Plus" },
  ];

  return (
    <>
      <Helmet>
        <title>Create Event - ExpenseLoop</title>
        <meta
          name="description"
          content="Create a new expense sharing event and invite participants to track shared costs"
        />
      </Helmet>
      <div className="min-h-screen bg-background">
        {/* <Header user={currentUser} isAuthenticated={true} /> */}

        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <Breadcrumb customItems={breadcrumbItems} />

            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-foreground mb-2">
                Create New Event
              </h1>
              <p className="text-muted-foreground">
                Set up a new expense sharing group to track costs and settle
                balances
              </p>
            </div>

            {/* Form Content */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Form Details */}
              <div className="lg:col-span-2 space-y-6">
                <EventDetailsForm
                  eventName={eventName}
                  setEventName={setEventName}
                  eventDate={eventDate}
                  setEventDate={setEventDate}
                  eventDescription={eventDescription}
                  setEventDescription={setEventDescription}
                  budget={budget} // Added budget prop
                  setBudget={setBudget} // Added setBudget prop
                  errors={errors}
                  isLoading={isLoading}
                />

                <div className="bg-card rounded-lg border border-border p-6 shadow-subtle">
                  <div className="mb-4">
                    <h2 className="text-lg font-semibold text-foreground mb-2">
                      Add Participants
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Invite people to join your expense sharing group
                    </p>
                  </div>

                  <ParticipantSearch
                    searchEmail={searchEmail}
                    setSearchEmail={setSearchEmail}
                    onAddParticipant={handleAddParticipant}
                    errors={errors}
                    isLoading={isLoading}
                  />
                </div>
              </div>

              {/* Right Column - Participants & Actions */}
              <div className="space-y-6">
                <ParticipantList
                  participants={participants}
                  onRemoveParticipant={handleRemoveParticipant}
                  isLoading={isLoading}
                />

                <CreateEventActions
                  onCreateEvent={handleCreateEvent}
                  isLoading={isLoading}
                  isFormValid={isFormValid}
                  participantCount={participants?.length}
                />
              </div>
            </div>

            {/* Submit Error */}
            {errors?.submit && (
              <div className="mt-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                <p className="text-sm text-destructive">{errors?.submit}</p>
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default CreateEvent;
